import React, { useEffect, useRef } from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { LoadingSpinner } from "../../../common/LoadingSpinner";
import { IReviewsProductTypeID } from "../../../models/nominals";
import {
    IReviewQuery,
    ISearchFacet,
    ISearchReviewArgs,
    ReviewSortOption,
} from "../../../models/reviews";
import { serializeFacetValues } from "../../../utils/reviews";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { defaultState } from "../defaults";
import { getParamsFromFacetValues, updateQueryParams } from "../history";
import {
    setFacetValues,
    toggleMobileSideNav,
    updateSorting,
} from "../reducers";
import { loadProducts, loadReviews } from "../thunks";
import { ReviewsViewer as ReviewsViewerComponent } from "./../components/ReviewsViewer";
import type { IReviewsAppOwnProps } from "./_base";

interface IProps extends IReviewsAppOwnProps {
    showSources?: boolean;
    writeReviewURL?: string;
    showAppliedFilters: boolean;
    isCondensedView?: boolean;
}

const buildReviewSearchQuery = (args: {
    brandID: IProps["brandID"];
    pageSize: IProps["pageSize"];
    displayedProductTypeIDWhitelist: IProps["displayedProductTypeIDWhitelist"];
    productTypeIDs: readonly IReviewsProductTypeID[];
    selectedSortOption: ReviewSortOption;
    facetValues: IReviewQuery;
    page: number;
}): ISearchReviewArgs => {
    let productTypeIDs = args.productTypeIDs;
    if (
        productTypeIDs.length <= 0 &&
        args.displayedProductTypeIDWhitelist &&
        args.displayedProductTypeIDWhitelist.length > 0
    ) {
        productTypeIDs = args.displayedProductTypeIDWhitelist;
    }
    const productTypeIDString = serializeFacetValues(productTypeIDs);
    const { product_type_id: _, ...facetValuesWithoutProductType } =
        args.facetValues;
    const query: ISearchReviewArgs = {
        brand_id: args.brandID,
        ordering: args.selectedSortOption,
        product_type_id: productTypeIDString ? productTypeIDString : undefined,
        page: args.page,
        page_size: args.pageSize,
        ...facetValuesWithoutProductType,
    };
    return query;
};

const listReviewsAsync = async (
    args: Parameters<typeof buildReviewSearchQuery>[0] & {
        dispatch: ReturnType<typeof useAppDispatch>;
    },
): Promise<void> => {
    const query = buildReviewSearchQuery(args);
    await args.dispatch(
        loadReviews({
            brandID: args.brandID,
            query,
        }),
    );
};

const listProductsAsync = async (
    brandID: IProps["brandID"],
    dispatch: ReturnType<typeof useAppDispatch>,
): Promise<void> => {
    await dispatch(loadProducts(brandID));
};

const initSourceFilter = (args: {
    sourceFilterWhitelist: IProps["sourceFilterWhitelist"];
    facetValues: IReviewQuery;
    facets: ReadonlyArray<ISearchFacet>;
    dispatch: ReturnType<typeof useAppDispatch>;
}): void => {
    const sourceFilterWhitelist = args.sourceFilterWhitelist;
    if (!sourceFilterWhitelist || sourceFilterWhitelist.length <= 0) {
        return;
    }
    const sourceFacet = args.facets.find((facet) => {
        return facet.type === "source_id";
    });
    if (!sourceFacet) {
        return;
    }
    const optionIDs = sourceFacet.options
        .filter((option): boolean => {
            return sourceFilterWhitelist.includes(`${option.name}`);
        })
        .map((option): number => {
            return option.option_id;
        });
    const sourceIDs = serializeFacetValues(optionIDs);
    if (args.facetValues.source_id !== sourceIDs) {
        args.dispatch(
            setFacetValues({
                ...args.facetValues,
                source_id: sourceIDs,
            }),
        );
    }
};

export const ReviewsViewer = (props: IProps): React.ReactNode => {
    const {
        brandID,
        defaultSortOption = ReviewSortOption.HIGHEST_RATED,
        pageSize = 6,
        displayedProductTypeIDWhitelist,
        sourceFilterWhitelist,
        starHasStroke,
        showSources = true,
        writeReviewURL = "",
        showAppliedFilters,
        isCondensedView,
    } = props;

    const state = useAppSelector(
        (rootState) => rootState.reviews || defaultState,
    );
    const dispatch = useAppDispatch();

    const listReviewsTimerRef = useRef<number | undefined>(undefined);

    const listReviewsDebounced = (page: number, delayMs = 100) => {
        const window = getWindow();
        if (!window) {
            return;
        }
        if (listReviewsTimerRef.current) {
            window.clearTimeout(listReviewsTimerRef.current);
        }
        listReviewsTimerRef.current = window.setTimeout(() => {
            void listReviewsAsync({
                brandID,
                pageSize,
                displayedProductTypeIDWhitelist,
                productTypeIDs: state.ui.productTypeIDs,
                selectedSortOption: state.ui.selectedSortOption,
                facetValues: state.ui.facetValues,
                page,
                dispatch,
            });
        }, delayMs);
    };

    const onLoadMore = async (): Promise<void> => {
        await listReviewsAsync({
            brandID,
            pageSize,
            displayedProductTypeIDWhitelist,
            productTypeIDs: state.ui.productTypeIDs,
            selectedSortOption: state.ui.selectedSortOption,
            facetValues: state.ui.facetValues,
            page: state.ui.page + 1,
            dispatch,
        });
    };

    const onToggleMobileSideNav = () => {
        dispatch(toggleMobileSideNav());
    };

    // Initial mount: load products, set default sort, and load reviews
    useEffect(() => {
        initSourceFilter({
            sourceFilterWhitelist,
            facetValues: state.ui.facetValues,
            facets: state.data.facets,
            dispatch,
        });
        void listProductsAsync(brandID, dispatch);
        dispatch(updateSorting(defaultSortOption));
        listReviewsDebounced(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update source filter when sourceFilterWhitelist or facets change
    useEffect(() => {
        initSourceFilter({
            sourceFilterWhitelist,
            facetValues: state.ui.facetValues,
            facets: state.data.facets,
            dispatch,
        });
    }, [
        sourceFilterWhitelist,
        state.data.facets,
        state.ui.facetValues,
        dispatch,
    ]);

    // Reload reviews when sort, filters, or product types change
    useEffect(() => {
        listReviewsDebounced(1);
    }, [
        state.ui.selectedSortOption,
        state.ui.productTypeIDs,
        state.ui.facetValues,
    ]);

    // Update query string to match current facet selections
    useEffect(() => {
        const params = getParamsFromFacetValues(
            state.ui.facetValues,
            state.ui.productTypeIDs,
        );
        updateQueryParams(params);
    }, [state.ui.facetValues, state.ui.productTypeIDs]);

    if (!state.data.loadedInitialProducts || !state.data.loadedInitialReviews) {
        return <LoadingSpinner />;
    }

    const showSourcesValue = !!showSources;
    return (
        <ReviewsViewerComponent
            displayedProductTypeIDWhitelist={
                displayedProductTypeIDWhitelist || []
            }
            enablePDPLinks={showSourcesValue}
            isCondensedView={isCondensedView}
            mobileSideNavOpen={state.ui.mobileSideNavOpen}
            onLoadMore={onLoadMore}
            onToggleMobileSideNav={onToggleMobileSideNav}
            showAppliedFilters={showAppliedFilters}
            showSources={showSourcesValue}
            starHasStroke={starHasStroke}
            writeReviewURL={writeReviewURL}
        />
    );
};
